import { useState } from "react";
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Loader2, LockKeyhole } from "lucide-react";
import api, { formatMoney, formatApiErrorDetail } from "../lib/apiClient";
import { Button } from "./ui/button";

export const PaymentForm = ({ total, createIntent, onPaid }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");

  const pay = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || processing) return;
    setProcessing(true);
    setError("");
    try {
      // 1) Deferred flow: validate the Payment Element first (required by Stripe).
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message || "Please check your card details and try again.");
        setProcessing(false);
        return;
      }
      // 2) Create the PaymentIntent server-side (authoritative amount).
      let intent;
      try {
        intent = await createIntent();
      } catch (err) {
        setError(err.response?.data?.detail ? formatApiErrorDetail(err.response.data.detail) : "Could not start the payment. No charge was made.");
        setProcessing(false);
        return;
      }
      // 3) Confirm on Stripe against the connected account.
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret: intent.clientSecret,
        confirmParams: { return_url: window.location.href },
        redirect: "if_required",
      });
      if (confirmError) {
        console.error("[checkout] Stripe confirm error:", confirmError);
        const parts = [confirmError.message || "Your payment could not be completed."];
        if (confirmError.code) parts.push(`(code: ${confirmError.code}${confirmError.decline_code ? `/${confirmError.decline_code}` : ""})`);
        setError(parts.join(" "));
        setProcessing(false);
        return;
      }
      if (paymentIntent && !["succeeded", "processing"].includes(paymentIntent.status)) {
        setError(`Payment not completed (status: ${paymentIntent.status}). No charge was captured.`);
        setProcessing(false);
        return;
      }
      // 4) Verify server-side before showing success.
      const { data } = await api.post("/checkout/verify", { order_number: intent.orderNumber, client_secret: intent.clientSecret }, { timeout: 30000 });
      if (data?.order?.status === "paid") {
        onPaid(data.order);
      } else {
        setError("Payment received but not yet confirmed. Please check your order status shortly.");
        setProcessing(false);
      }
    } catch (err) {
      setError(err.response?.data?.detail ? formatApiErrorDetail(err.response.data.detail) : "We could not complete the payment. Please try again.");
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={pay} className="space-y-5" data-testid="payment-form">
      <PaymentElement options={{ layout: "tabs" }} />
      {error && <p role="alert" className="text-sm text-destructive p-3 rounded-xl border border-destructive/30" data-testid="payment-error">{error}</p>}
      <Button type="submit" disabled={!stripe || processing} data-testid="pay-now-button" className="w-full min-h-12 h-auto py-3 rounded-xl font-semibold">
        {processing ? <><Loader2 className="animate-spin" aria-hidden="true" /> Processing payment…</> : <><LockKeyhole size={16} aria-hidden="true" /> Pay {formatMoney(total)}</>}
      </Button>
      <p className="text-xs text-muted-foreground text-center" data-testid="payment-secure-note">Secured by Attestly · card details are handled by the payment provider.</p>
    </form>
  );
};
